import { blue } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import fi from 'date-fns/locale/fi'
import { Chart, ChartData, ChartDataset, ChartOptions, ScatterDataPoint } from 'chart.js'
import 'chartjs-adapter-date-fns'
import {
  getDifferenceOfDateRangeInHours,
  humanizeDate,
  humanizeDateRange,
  humanizeTimestamp
} from '../utils/date'
import LoadingState from './LoadingState'
import _ from 'lodash'
import LineChart from './LineChart'
import { useTheme } from '@mui/material'
import { DateRange, RuuviTagBatteryAggregate, RuuviTagBatteryAggregateStatistic } from '../types'
import { useDispatch } from 'react-redux'
import TimePeriod from '../enums/TimePeriod'
import { setDateRange, setTimePeriod } from '../redux/slices/query'

export interface RuuviTagBatteryAggregateChart {
  batteries: RuuviTagBatteryAggregate[]
  dateRange: DateRange
  statistics: RuuviTagBatteryAggregateStatistic[]
  options?: ChartOptions
  height?: string
  loading?: boolean
}

export default function RuuviTagBatteryAggregateChart({
  batteries,
  dateRange,
  statistics,
  options,
  height = '320px',
  loading = false
}: RuuviTagBatteryAggregateChart) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The difference of date range as hours.
   */
  const differenceOfDateRangeInHours = getDifferenceOfDateRangeInHours(dateRange.from, dateRange.to)

  /**
   * Update the date range.
   */
  function updateDateRange({ chart }: { chart: Chart }) {
    const { min, max } = chart.scales.x

    dispatch(setTimePeriod(TimePeriod.CUSTOM))
    dispatch(
      setDateRange({
        from: new Date(parseInt(min.toString())),
        to: new Date(parseInt(max.toString()))
      })
    )
  }

  /**
   * The chart data.
   */
  const data: ChartData = {
    datasets: makeChartDatasets()
  }

  /**
   * The default chart options.
   */
  const defaultOptions: ChartOptions<'line'> = {
    plugins: {
      zoom: {
        zoom: {
          drag: {
            enabled: true
          },
          mode: 'x',
          onZoomComplete: updateDateRange
        }
      },
      title: {
        display: true,
        position: 'bottom',
        padding: {
          top: 24
        },
        text: `${humanizeDateRange(dateRange.from, dateRange.to)}`,
        font: {
          // @ts-expect-error typed incorrectly
          weight: '600',
          size: 14
        },
        color: theme.palette.text.primary
      },
      legend: {
        display: true,
        labels: {
          boxWidth: 24,
          boxHeight: 1,
          padding: 24,
          font: {
            // @ts-expect-error typed incorrectly
            weight: '600',
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems): string => {
            if (tooltipItems.length) {
              return differenceOfDateRangeInHours > 744
                ? // @ts-expect-error typed incorrectly
                  humanizeDate(tooltipItems[0].raw.x)
                : // @ts-expect-error typed incorrectly
                  humanizeTimestamp(tooltipItems[0].raw.x)
            }

            return ''
          }
        }
      },
      decimation: {
        enabled: false,
        algorithm: 'min-max'
      }
    },
    elements: {
      point: {
        pointStyle: 'point',
        radius: 0
      },
      line: {
        borderWidth: 1
      }
    },
    scales: {
      x: {
        type: 'time',
        display: true,
        // @ts-expect-error typed incorrectly
        min: dateRange.from,
        // @ts-expect-error typed incorrectly
        max: dateRange.to,
        time: {
          unit:
            differenceOfDateRangeInHours <= 3
              ? 'minute'
              : differenceOfDateRangeInHours <= 24
                ? 'hour'
                : 'day',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
            day: 'd.M'
          }
        },
        adapters: {
          date: {
            locale: fi
          }
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          source: 'auto'
        },
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        },
        color: '#fff'
      },
      y: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        }
      }
    }
  }

  /**
   * The chart options.
   */
  options = _.merge(defaultOptions, options)

  /**
   * Make the chart datasets.
   */
  function makeChartDatasets(): ChartDataset[] {
    const datasets: ChartDataset[] = []

    for (const statistic of statistics) {
      datasets.push(makeChartDataset(statistic))
    }

    return datasets
  }

  /**
   * Make the chart dataset.
   */
  function makeChartDataset(statistic: RuuviTagBatteryAggregateStatistic): ChartDataset {
    const label = makeDatasetLabel(statistic)
    const color = makeDatasetColor(statistic)
    const key = makeKey(statistic)
    const data = batteries.map((battery) => {
      return {
        x: battery.timeBucket,
        y: battery[key]
      }
    })

    return {
      label,
      backgroundColor: color,
      borderColor: color,
      tension: 0.4,
      spanGaps: false,
      data: data as unknown as ScatterDataPoint[]
    }
  }

  /**
   * Make the key.
   */
  function makeKey(statistic: RuuviTagBatteryAggregateStatistic): keyof RuuviTagBatteryAggregate {
    return _.camelCase(`voltage_${statistic}`) as keyof RuuviTagBatteryAggregate
  }

  /**
   * Make the label for the dataset.
   */
  function makeDatasetLabel(statistic: RuuviTagBatteryAggregateStatistic): string {
    return `${t('voltage')} (${t(statistic)})`
  }

  /**
   * Make the color for the dataset.
   */
  function makeDatasetColor(statistic: RuuviTagBatteryAggregateStatistic) {
    let weight: 'A100' | 'A700' | '500'
    switch (statistic) {
      case 'min':
        weight = 'A100'
        break
      case 'max':
        weight = 'A700'
        break
      case 'avg':
      default:
        weight = '500'
        break
    }

    // @see https://mui.com/material-ui/customization/color/#color-palette
    return blue[weight]
  }

  return (
    <>
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <LineChart data={data} options={options} wrapperHeight={height} />
        </>
      )}
    </>
  )
}
